// Components
import { Input, Form, Button as ANTButton } from "antd";
import { Head } from "components/molecules/Head/Head";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import HeaderStatusInfo from "components/molecules/HeaderStatusInfo/HeaderStatusInfo";
import Label from "components/atoms/Label/Label";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import Spinner from "components/atoms/Spinner/Spinner";

// Hooks
import { useCapitaStarDelinkPage } from "containers/CapitaStarDelinkPage/useCapitaStarDelinkPage";

// Styles
import styles from "containers/CapitaStarDelinkPage/CapitaStarDelinkPage.module.css";
import theme from "styles/theme";

// Constants & Utils
import { PageTitles, StatusInfo, KrisFlyerDelink, CapitaStarDelink as Constants, VehicleIUDelink } from "constants/global";
import getIcon from "utils/getIcon";
import getIconOthers from "utils/getIconCapitaStarDelink";

type CapitaStarDelinkPageProps = {
	vehicleVariant?: boolean;
	krisflyerVariant?: boolean;
};

const CapitaStarDelinkPage = ({ vehicleVariant, krisflyerVariant }: CapitaStarDelinkPageProps): JSX.Element => {
	const {
		inputFields,
		capitaStarNumber,
		form,
		initValues,
		loading,
		delinkloading,
		memberloading,
		fieldErrors,
		vehicleIUNumber,
		KrisFlyerNumber,
		handleVehicleIUSubmit,
		handleKrisFlyerSubmit,
		getFieldValue,
		handleCardNumChange,
		handleUserIdChange,
		handleMemberIdSearch,
		handleSubmit
	} = useCapitaStarDelinkPage();

	const generateBorderStyle = (fieldValue?: string, fieldErrorValue?: boolean) => {
		if (fieldValue?.length && fieldErrorValue) {
			return styles?.err;
		} else if (fieldValue?.length && !fieldErrorValue) {
			return styles?.filled;
		}
		return "";
	};

	const generateBtnClassName = (btnId: string) => {
		return getFieldValue(btnId, form) ? styles?.btn_enabled : styles?.btn_disabled;
	};

	const generateHandler = (btnId: string) => {
		if (btnId === Constants.FORM_LABELS.VEHICLE_DELINK && getFieldValue(btnId, form)) {
			return handleVehicleIUSubmit;
		}
		if (btnId === Constants.FORM_LABELS.CAPITA_DELINK && getFieldValue(btnId, form)) {
			return handleSubmit;
		}
		if (btnId === Constants.FORM_LABELS.KRISFLYER_DELINK && getFieldValue(btnId, form)) {
			return handleKrisFlyerSubmit;
		}
		return undefined;
	};

	// Extract nested ternary for page title
	const getPageTitle = () => {
		if (vehicleVariant) {
			return PageTitles?.VEHICLE_DELINK;
		} else if (krisflyerVariant) {
			return PageTitles?.KRISFLYER_DELINK;
		} else {
			return PageTitles?.CAPITASTAR_DELINK;
		}
	};

	// Extract nested ternary for title and subtitle
	const getTitleAndSubtitle = () => {
		if (vehicleVariant) {
			return {
				title: VehicleIUDelink.PAGE_TITLE,
				subTitle: VehicleIUDelink.PAGE_SUBTITLE
			};
		} else if (krisflyerVariant) {
			return {
				title: KrisFlyerDelink.PAGE_TITLE,
				subTitle: KrisFlyerDelink.PAGE_SUBTITLE
			};
		} else {
			return {
				title: Constants.PAGE_TITLE,
				subTitle: Constants.PAGE_SUBTITLE
			};
		}
	};

	// Extract nested ternary for form item rendering
	const renderMemberIdFormItem = () => {
		if (vehicleVariant) {
			return (
				<Form.Item
					name={Constants.FORM_LABELS.VEHICLE_DELINK}
					initialValue={vehicleIUNumber}
					label={<Label label={VehicleIUDelink.MEMBER_ID_LABEL} classes={{ label: styles?.basic_input_label }} />}
				>
					<Input disabled className={`${styles?.member_id_display}`} value={vehicleIUNumber} />
				</Form.Item>
			);
		} else if (krisflyerVariant) {
			return (
				<Form.Item
					name={Constants.FORM_LABELS.KRISFLYER_DELINK}
					initialValue={KrisFlyerNumber}
					label={<Label label={KrisFlyerDelink.MEMBER_ID_LABEL} classes={{ label: styles?.basic_input_label }} />}
				>
					<Input disabled className={`${styles?.member_id_display}`} value={KrisFlyerNumber} />
				</Form.Item>
			);
		} else {
			return (
				<Form.Item
					name={Constants.FORM_LABELS.CAPITA_DELINK}
					initialValue={capitaStarNumber}
					label={<Label label={Constants.MEMBER_ID_LABEL} classes={{ label: styles?.basic_input_label }} />}
				>
					<Input disabled className={`${styles?.member_id_display}`} value={capitaStarNumber} />
				</Form.Item>
			);
		}
	};

	// Extract nested ternary for button rendering
	const renderActionButton = () => {
		if (vehicleVariant) {
			return (
				<ANTButton className={generateBtnClassName(Constants.FORM_LABELS.VEHICLE_DELINK)} onClick={generateHandler(Constants.FORM_LABELS.VEHICLE_DELINK)}>
					{Constants.BUTTON_LABEL}
				</ANTButton>
			);
		} else if (krisflyerVariant) {
			return (
				<ANTButton className={generateBtnClassName(Constants.FORM_LABELS.KRISFLYER_DELINK)} onClick={generateHandler(Constants.FORM_LABELS.KRISFLYER_DELINK)}>
					{Constants.BUTTON_LABEL}
				</ANTButton>
			);
		} else {
			return (
				<ANTButton className={generateBtnClassName(Constants.FORM_LABELS.CAPITA_DELINK)} onClick={generateHandler(Constants.FORM_LABELS.CAPITA_DELINK)}>
					{Constants.BUTTON_LABEL}
				</ANTButton>
			);
		}
	};

	const titleAndSubtitle = getTitleAndSubtitle();

	return (
		<div className="min-h-screen relative">
			{(delinkloading || loading || memberloading) && <Spinner type="transparent" />}
			<div className={styles?.bg_curve}>{getIconOthers({ icon: "background" })}</div>
			<div className={styles?.bg_frame}>{getIconOthers({ icon: "overlay" })}</div>

			<div className={styles?.root}>
				<Head>{getPageTitle()}</Head>
				<HeaderStatusInfo background="green" message={StatusInfo.NO_ACTION_REQ} />
				<PageTitle title={titleAndSubtitle.title} subTitle={titleAndSubtitle.subTitle} />
				<div className={styles?.main_wrapper}>
					<Form className={styles?.form} requiredMark={false} form={form} layout="vertical">
						<p className={styles?.search_title_text}>{Constants.SEARCH_TITLE}</p>

						<div className={styles?.search_wrapper}>
							<Form.Item
								name={Constants.FORM_LABELS.CARD_NUM}
								initialValue={initValues?.cardNumber}
								extra={fieldErrors?.cardNumber && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGES.CARD_NUM} />}
							>
								<Input
									className={`${styles?.card_number_input} ${generateBorderStyle(inputFields?.cardNumber, fieldErrors?.cardNumber)}`}
									placeholder={Constants.CARD_NUM_PLACEHOLDER}
									value={inputFields?.cardNumber}
									onChange={(e) => handleCardNumChange(e)}
									pattern="[0-9]"
								/>
							</Form.Item>
							<div className={styles?.ortxt}>or</div>
							<Form.Item
								name={Constants.FORM_LABELS.USER_ID}
								initialValue={initValues?.userId}
								extra={fieldErrors?.userId && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGES.USER_ID} />}
							>
								<Input
									className={`${styles?.user_id_input} ${generateBorderStyle(inputFields?.userId, fieldErrors?.userId)}`}
									placeholder={Constants.USER_ID_PLACEHOLDER}
									value={inputFields?.userId}
									onChange={(e) => handleUserIdChange(e)}
								/>
							</Form.Item>
						</div>
						{!fieldErrors?.cardNumber && !fieldErrors?.userId && (
							<div className={styles?.bottom_text_link}>
								<p className={styles?.textViolet} onClick={handleMemberIdSearch}>
									{Constants.SEARCH_TEXT}
								</p>
								<span className="ml-[0.5625rem] hover:cursor-pointer" onClick={handleMemberIdSearch}>
									{getIcon({ icon: "inWindow", iconColor: theme.colors.violet })}
								</span>
							</div>
						)}

						{renderMemberIdFormItem()}

						<div className={styles?.btn_wrapper}>{renderActionButton()}</div>
					</Form>
				</div>
			</div>
		</div>
	);
};

export default CapitaStarDelinkPage;
